import { type FormEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CATALOG_FILTER_EXISTS } from '@backstage/catalog-client';
import { parseEntityRef, stringifyEntityRef } from '@backstage/catalog-model';
import { configApiRef, identityApiRef, useApi } from '@backstage/frontend-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  type LogEvent,
  type ScaffolderTask,
  type TemplateParameterSchema,
  scaffolderApiRef,
} from '@backstage/plugin-scaffolder-react';
import { useMyTeams } from './forgejo';
import { HiddenOrganisationHint, NEW_ORGANISATION_HREF, NoTeamDialog } from './TeamHints';

const TASK_LIMIT = 50;

const STATUS: Record<string, { text: string; color: string }> = {
  open: { text: 'Wacht', color: 'neutral' },
  processing: { text: 'Bezig', color: 'accent' },
  completed: { text: 'Klaar', color: 'success' },
  failed: { text: 'Mislukt', color: 'critical' },
  cancelled: { text: 'Geannuleerd', color: 'neutral' },
  skipped: { text: 'Overgeslagen', color: 'neutral' },
};

const status = (value: string) => STATUS[value] ?? { text: value, color: 'neutral' };

const formatMoment = (value: string) =>
  new Date(value).toLocaleString('nl-NL', { dateStyle: 'medium', timeStyle: 'short' });

const templateTitle = (task: ScaffolderTask) => {
  const template = task.spec.templateInfo?.entity?.metadata;
  return template?.title ?? template?.name ?? 'Project';
};

const taskSubject = (task: ScaffolderTask) => {
  const { name, component } = task.spec.parameters ?? {};
  return String(name ?? component ?? '');
};

function Loading() {
  return (
    <nldd-container layout="row" gap="8" vertical-alignment="center">
      <nldd-activity-indicator />
      <nldd-text color="secondary">Laden</nldd-text>
    </nldd-container>
  );
}

export function TaskListPage() {
  const scaffolderApi = useApi(scaffolderApiRef);
  const [params, setParams] = useSearchParams();
  const [tasks, setTasks] = useState<ScaffolderTask[]>();
  const [failed, setFailed] = useState(false);
  const scope = useRef<HTMLElement>(null);
  const everyone = params.get('van') === 'iedereen';

  useEffect(() => {
    let current = true;
    setTasks(undefined);
    scaffolderApi
      .listTasks({ filterByOwnership: everyone ? 'all' : 'owned', limit: TASK_LIMIT })
      .then(result => current && setTasks(result.tasks))
      .catch(() => current && setFailed(true));
    return () => {
      current = false;
    };
  }, [scaffolderApi, everyone]);

  useEffect(() => {
    const element = scope.current;
    const onChange = (event: Event) => {
      const value = (event as CustomEvent<{ value?: string }>).detail?.value;
      setParams(value === 'iedereen' ? { van: 'iedereen' } : {});
    };
    element?.addEventListener('change', onChange);
    return () => element?.removeEventListener('change', onChange);
  }, [setParams]);

  return (
    <nldd-simple-section>
      <nldd-text color="secondary">Wat er is aangemaakt, en of dat gelukt is. Je ziet de laatste {TASK_LIMIT}.</nldd-text>
      <nldd-spacer size="16" />
      <nldd-segmented-control
        ref={scope}
        width="fit-content"
        accessible-label="Taken van"
        value={everyone ? 'iedereen' : 'mij'}
      >
        <nldd-segmented-control-item value="mij" text="Van mij" />
        <nldd-segmented-control-item value="iedereen" text="Van iedereen" />
      </nldd-segmented-control>
      <nldd-spacer size="16" />
      {failed ? (
        <nldd-banner variant="critical" text="De taken zijn nu niet beschikbaar" supporting-text="Probeer het later opnieuw." />
      ) : null}
      {!failed && !tasks ? <Loading /> : null}
      {tasks?.length === 0 ? (
        <nldd-inline-dialog text="Nog geen taken" supporting-text="Start een nieuw project, dan zie je het hier terug." />
      ) : null}
      {tasks?.length ? (
        <nldd-list appearance="box-base" type="navigation" accessible-label="Taken">
          {tasks.map(task => (
            <nldd-list-item key={task.id} href={`/create/tasks/${task.id}`}>
              <nldd-title-cell
                text={templateTitle(task)}
                supporting-text={[taskSubject(task), formatMoment(task.createdAt)].filter(Boolean).join(' · ')}
              />
              <nldd-spacer-cell size="12" />
              <nldd-cell width="fit-content">
                <nldd-badge color={status(task.status).color} text={status(task.status).text} />
              </nldd-cell>
            </nldd-list-item>
          ))}
        </nldd-list>
      ) : null}
    </nldd-simple-section>
  );
}

type OutputLink = { title?: string; url?: string; entityRef?: string };

const linkHref = (link: OutputLink) => {
  if (link.entityRef) {
    const entity = parseEntityRef(link.entityRef);
    return `/catalog/${entity.namespace}/${entity.kind.toLowerCase()}/${entity.name}`;
  }
  return link.url;
};

// The scaffolder colours its log lines for a terminal.
// eslint-disable-next-line no-control-regex
const plainLog = (message: string) => message.replace(/\u001b\[[0-9;]*m/g, '').trim();

const stepCellStatus = (value: string) => {
  if (value === 'open') {
    return 'future';
  }
  return value === 'processing' || value === 'failed' ? 'current' : 'past';
};

const stepPosition = (index: number, count: number) => {
  if (count === 1) {
    return 'only';
  }
  if (index === 0) {
    return 'first';
  }
  return index === count - 1 ? 'last' : 'between';
};

export function TaskPage() {
  const { taskId = '' } = useParams();
  const scaffolderApi = useApi(scaffolderApiRef);
  const [task, setTask] = useState<ScaffolderTask>();
  const [events, setEvents] = useState<LogEvent[]>([]);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setEvents([]);
    scaffolderApi.getTask(taskId).then(setTask).catch(() => setFailed(true));
    const subscription = scaffolderApi.streamLogs({ taskId }).subscribe({
      next: event => setEvents(current => [...current, event]),
      error: () => setFailed(true),
    });
    return () => subscription.unsubscribe();
  }, [scaffolderApi, taskId]);

  const finished = events.find(event => event.type === 'completion' || event.type === 'cancelled');

  useEffect(() => {
    if (finished) {
      scaffolderApi.getTask(taskId).then(setTask).catch(() => setFailed(true));
    }
  }, [scaffolderApi, taskId, finished]);

  const stepStatus = (stepId: string) =>
    events.filter(event => event.body.stepId === stepId && event.body.status).at(-1)?.body.status ?? 'open';
  const stepError = (stepId: string) =>
    events.filter(event => event.body.stepId === stepId && event.type === 'log' && !event.body.status).at(-1)?.body
      .message ?? '';

  if (failed && !task) {
    return (
      <nldd-simple-section>
        <nldd-banner variant="critical" text="Deze taak is niet gevonden" supporting-text="Bekijk je andere taken in het overzicht." />
      </nldd-simple-section>
    );
  }
  if (!task) {
    return (
      <nldd-simple-section>
        <Loading />
      </nldd-simple-section>
    );
  }

  const output = (finished?.body as { output?: { links?: OutputLink[] } } | undefined)?.output;
  const links = (output?.links ?? []).filter(link => linkHref(link));
  const steps = task.spec.steps;
  const templateRef = task.spec.templateInfo?.entityRef;
  const retryHref = templateRef
    ? `/create/templates/${parseEntityRef(templateRef).namespace}/${parseEntityRef(templateRef).name}`
    : '/create';

  return (
    <nldd-simple-section>
      <nldd-title
        slot="header"
        size="2"
        heading-level="2"
        text={templateTitle(task)}
        supporting-text={[taskSubject(task), formatMoment(task.createdAt)].filter(Boolean).join(' · ')}
      />
      {task.status === 'completed' ? (
        <nldd-banner variant="success" text="Het is gelukt" supporting-text="Alle stappen zijn uitgevoerd." />
      ) : null}
      {task.status === 'failed' ? (
        <nldd-banner variant="critical" text="Dit is niet gelukt" supporting-text="Bij de stap die misging staat de reden.">
          <nldd-button slot="actions" appearance="secondary" text="Opnieuw invullen" href={retryHref} />
        </nldd-banner>
      ) : null}
      {task.status === 'completed' || task.status === 'failed' ? <nldd-spacer size="16" /> : null}
      <nldd-list appearance="box-base" accessible-label="Stappen">
        {steps.map((step, index) => {
          const current = stepStatus(step.id);
          return (
            <nldd-list-item key={step.id}>
              <nldd-step-cell status={stepCellStatus(current)} position={stepPosition(index, steps.length)} />
              <nldd-spacer-cell size="12" />
              <nldd-text-cell
                text={step.name}
                color={current === 'failed' ? 'critical' : undefined}
                supporting-text={current === 'failed' ? plainLog(stepError(step.id)) : undefined}
              />
              {current === 'open' ? null : (
                <>
                  <nldd-spacer-cell size="12" />
                  <nldd-cell width="fit-content">
                    <nldd-badge color={status(current).color} text={status(current).text} />
                  </nldd-cell>
                </>
              )}
            </nldd-list-item>
          );
        })}
      </nldd-list>
      {links.length ? (
        <>
          <nldd-spacer size="24" />
          <nldd-button-group>
            {links.map((link, index) => (
              <nldd-button
                key={link.title ?? link.url ?? link.entityRef}
                appearance={index === 0 ? 'primary' : 'secondary'}
                text={link.title ?? 'Openen'}
                href={linkHref(link)}
              />
            ))}
          </nldd-button-group>
        </>
      ) : null}
    </nldd-simple-section>
  );
}

type Property = {
  title?: string;
  description?: string;
  type?: string;
  pattern?: string;
  enum?: string[];
  enumNames?: string[];
  'ui:field'?: string;
  'ui:widget'?: string;
  'ui:options'?: { catalogFilter?: Record<string, unknown> };
};

type Step = TemplateParameterSchema['steps'][number];

const KNOWN_FIELDS = [undefined, 'EntityNamePicker', 'MyGroupsPicker', 'EntityPicker', 'CodeOrganisationPicker'];
const CONDITIONAL_KEYWORDS = ['dependencies', 'if', 'allOf', 'anyOf', 'oneOf'];

const stepProperties = (step: Step) =>
  Object.entries((step.schema.properties ?? {}) as Record<string, Property>);

// Anything this form cannot render honestly goes to Backstage's own form instead.
const canRender = (schema: TemplateParameterSchema) =>
  schema.steps.every(
    step =>
      !CONDITIONAL_KEYWORDS.some(keyword => keyword in step.schema) &&
      stepProperties(step).every(
        ([, property]) => property.type === 'string' && KNOWN_FIELDS.includes(property['ui:field']),
      ),
  );

type Option = { value: string; text: string };

/** Teams from the catalog, for when code.overheid.nl cannot be asked. */
function useCatalogGroups(enabled: boolean) {
  const catalogApi = useApi(catalogApiRef);
  const identityApi = useApi(identityApiRef);
  const [options, setOptions] = useState<Option[]>();
  useEffect(() => {
    if (!enabled) {
      return;
    }
    (async () => {
      const { userEntityRef } = await identityApi.getBackstageIdentity();
      const { items } = await catalogApi.getEntities({
        filter: { kind: 'Group', 'relations.hasMember': [userEntityRef] },
        fields: ['kind', 'metadata'],
      });
      setOptions(
        items.map(item => ({ value: stringifyEntityRef(item), text: item.metadata.title ?? item.metadata.name })),
      );
    })().catch(() => setOptions([]));
  }, [catalogApi, identityApi, enabled]);
  return options;
}

/**
 * The person's teams, live from code.overheid.nl, so a team made or joined a moment ago is
 * there. Reading them also writes them to the catalog.
 */
function useMemberGroups() {
  const { myTeams, failure } = useMyTeams();
  const fallback = useCatalogGroups(Boolean(failure));
  if (failure) {
    return { options: fallback };
  }
  return {
    myTeams,
    options: myTeams?.teams.map(team => ({ value: team.entityRef, text: team.title })),
  };
}

function useEntityOptions(catalogFilter: Record<string, unknown> | undefined) {
  const catalogApi = useApi(catalogApiRef);
  const [options, setOptions] = useState<Option[]>();
  const serialized = JSON.stringify(catalogFilter ?? {});
  useEffect(() => {
    const filter = Object.fromEntries(
      Object.entries(JSON.parse(serialized)).map(([key, value]) => [
        key,
        value === 'CATALOG_FILTER_EXISTS' ? CATALOG_FILTER_EXISTS : value,
      ]),
    ) as Record<string, string>;
    catalogApi
      .getEntities({ filter, fields: ['kind', 'metadata'], order: { field: 'metadata.name', order: 'asc' } })
      .then(({ items }) =>
        setOptions(
          items.map(item => ({ value: stringifyEntityRef(item), text: item.metadata.title ?? item.metadata.name })),
        ),
      )
      .catch(() => setOptions([]));
  }, [catalogApi, serialized]);
  return options;
}

function Choice(props: { name: string; required: boolean; options: Option[]; placeholder: string }) {
  const { options } = props;
  return (
    <nldd-dropdown required={props.required ? '' : undefined}>
      <select name={props.name} defaultValue={options.length === 1 ? options[0].value : ''}>
        {options.length === 1 ? null : <option value="">{props.placeholder}</option>}
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.text}
          </option>
        ))}
      </select>
    </nldd-dropdown>
  );
}

function GroupInput(props: { name: string; required: boolean }) {
  const { myTeams, options } = useMemberGroups();
  const codeUrl = useApi(configApiRef).getString('platform.codeUrl').replace(/\/$/, '');
  if (!options) {
    return <Loading />;
  }
  if (options.length === 0) {
    return <NoTeamDialog myTeams={myTeams} codeUrl={codeUrl} />;
  }
  return <Choice name={props.name} required={props.required} options={options} placeholder="Kies een team" />;
}

/** Organisations the bot manages and the person is in: only there may they add a team. */
function OrganisationInput(props: { name: string; required: boolean }) {
  const { myTeams, failure } = useMyTeams();
  if (failure) {
    return <nldd-text color="secondary">{failure}</nldd-text>;
  }
  if (!myTeams) {
    return <Loading />;
  }
  const options = myTeams.organisations
    .filter(organisation => organisation.isMember)
    .map(organisation => ({
      value: organisation.name,
      text: organisation.title === organisation.name ? organisation.name : `${organisation.title} (${organisation.name})`,
    }));
  if (options.length === 0) {
    return (
      <>
        <nldd-inline-dialog
          icon="account"
          text="Je zit nog niet in een organisatie"
          supporting-text="Een team hoort bij een organisatie op code.overheid.nl. Maak er een, dan krijg je meteen een eerste team."
        >
          <nldd-button slot="actions" appearance="primary" href={NEW_ORGANISATION_HREF} text="Maak een organisatie" />
        </nldd-inline-dialog>
        <nldd-spacer size="16" />
        <HiddenOrganisationHint myTeams={myTeams} />
      </>
    );
  }
  return <Choice name={props.name} required={props.required} options={options} placeholder="Kies een organisatie" />;
}

function EntityInput(props: { name: string; required: boolean; property: Property }) {
  const options = useEntityOptions(props.property['ui:options']?.catalogFilter);
  if (!options) {
    return <Loading />;
  }
  return (
    <nldd-combo-box name={props.name} required={props.required ? '' : undefined} placeholder="Typ om te zoeken">
      <nldd-menu>
        {options.map(option => (
          <nldd-menu-item key={option.value} value={option.value} text={option.text} />
        ))}
      </nldd-menu>
    </nldd-combo-box>
  );
}

function Field(props: { name: string; required: boolean; property: Property }) {
  const { name, required, property } = props;
  const requiredAttribute = required ? '' : undefined;
  const field = useRef<HTMLElement>(null);

  // nldd-validation-list only follows `input`, and choosing in a combo box gives `change`. The
  // field then keeps saying a value is missing, and the first submit is refused.
  useEffect(() => {
    const element = field.current;
    const onChange = ({ target }: Event) => {
      if (target instanceof HTMLElement && target.localName === 'nldd-combo-box') {
        target.dispatchEvent(new Event('input'));
      }
    };
    element?.addEventListener('change', onChange);
    return () => element?.removeEventListener('change', onChange);
  }, []);

  const picker = property['ui:field'];
  let input: ReactNode;
  if (picker === 'MyGroupsPicker') {
    input = <GroupInput name={name} required={required} />;
  } else if (picker === 'CodeOrganisationPicker') {
    input = <OrganisationInput name={name} required={required} />;
  } else if (picker === 'EntityPicker') {
    input = <EntityInput name={name} required={required} property={property} />;
  } else if (property.enum) {
    input = (
      <nldd-dropdown required={requiredAttribute}>
        <select name={name}>
          {property.enum.map((value, index) => (
            <option key={value} value={value}>
              {property.enumNames?.[index] ?? value}
            </option>
          ))}
        </select>
      </nldd-dropdown>
    );
  } else if (property['ui:widget'] === 'textarea') {
    input = <nldd-multi-line-text-field name={name} required={requiredAttribute} />;
  } else {
    input = <nldd-text-field name={name} required={requiredAttribute} pattern={property.pattern} no-spellcheck="" />;
  }
  return (
    <nldd-form-field ref={field} label={property.title ?? name} optional={required ? undefined : ''}>
      {input}
      <nldd-validation-list>
        {required ? (
          <nldd-validation-item id={`${name}-verplicht`} required="">
            {property.title ?? name} is nodig.
          </nldd-validation-item>
        ) : null}
        {property.pattern ? (
          <nldd-validation-item id={`${name}-vorm`} match={property.pattern}>
            {property.description ?? 'De waarde heeft niet de juiste vorm.'}
          </nldd-validation-item>
        ) : null}
      </nldd-validation-list>
      {property.description && !property.pattern ? (
        <nldd-form-field-help-text>{property.description}</nldd-form-field-help-text>
      ) : null}
    </nldd-form-field>
  );
}

function StepFields({ step }: { step: Step }) {
  const required = (step.schema.required ?? []) as string[];
  return (
    <>
      {stepProperties(step).map(([name, property]) => (
        <Field key={name} name={name} required={required.includes(name)} property={property} />
      ))}
    </>
  );
}

/**
 * The template's parameters as one NLDD form. Rendered at the splat route, so `fallback`, which
 * brings its own relative routes, still resolves against the templates root.
 */
export function TemplateFormPage(props: { fallback: ReactNode }) {
  const [namespace, name] = (useParams()['*'] ?? '').split('/');
  const templateRef = `template:${namespace}/${name}`;
  const scaffolderApi = useApi(scaffolderApiRef);
  const navigate = useNavigate();
  const [schema, setSchema] = useState<TemplateParameterSchema>();
  const [missing, setMissing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    scaffolderApi.getTemplateParameterSchema(templateRef).then(setSchema).catch(() => setMissing(true));
  }, [scaffolderApi, templateRef]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setFailed(false);
    try {
      const values = Object.fromEntries(
        [...new FormData(event.currentTarget).entries()].filter(([, value]) => value !== ''),
      ) as Record<string, string>;
      const { taskId } = await scaffolderApi.scaffold({ templateRef, values, secrets: {} });
      navigate(`/create/tasks/${taskId}`);
    } catch {
      setFailed(true);
      setSubmitting(false);
    }
  };

  if (missing) {
    return (
      <nldd-simple-section>
        <nldd-banner variant="critical" text="Deze template is niet gevonden" supporting-text="Kies een andere template in het overzicht." />
      </nldd-simple-section>
    );
  }
  if (!schema) {
    return (
      <nldd-simple-section>
        <Loading />
      </nldd-simple-section>
    );
  }
  if (!canRender(schema)) {
    return <>{props.fallback}</>;
  }

  return (
    <nldd-simple-section>
      <nldd-title slot="header" size="2" heading-level="2" text={schema.title} supporting-text={schema.description} />
      {failed ? (
        <>
          <nldd-banner variant="critical" text="Starten is niet gelukt" supporting-text="Er is niets aangemaakt. Probeer het opnieuw." />
          <nldd-spacer size="16" />
        </>
      ) : null}
      <nldd-form>
        <form onSubmit={submit}>
          {schema.steps.length === 1 ? (
            <StepFields step={schema.steps[0]} />
          ) : (
            schema.steps.map(step => (
              <nldd-form-section key={step.title} text={step.title} supporting-text={step.description}>
                <StepFields step={step} />
              </nldd-form-section>
            ))
          )}
          <nldd-form-actions>
            <nldd-button-group>
              <nldd-button
                appearance="primary"
                type="submit"
                text="Maak aan"
                loading={submitting ? '' : undefined}
                disabled={submitting ? '' : undefined}
              />
            </nldd-button-group>
          </nldd-form-actions>
        </form>
      </nldd-form>
    </nldd-simple-section>
  );
}
